import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GameType } from "@/const";
import { useRequest } from "@components/AppHook";
import { tmrPushEvent } from "@components/Counters";
import { DevForm, FormFieldErrorMessage, FormFieldGroup, FormFieldHelpText } from "@components/Forms/Fields";
import { Awesome, DevModal } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { parseQS, url_reverse } from "@components/Utils";

const useToggleCard = (k, gameId) => {
    let gids = JSON.parse(localStorage.getItem(k) || "[]");
    const [hiddenCard, setHiddenCard] = useState(gids.includes(gameId));

    const toggleVisibility = () => {
        if (hiddenCard) {
            gids = gids.filter((v) => v !== gameId);
        } else {
            gids.push(gameId);
        }
        localStorage.setItem(k, JSON.stringify(gids));
        setHiddenCard(!hiddenCard);
    };

    const ToggleCardBtn = () => {
        return <Awesome type="sort" onClick={toggleVisibility} />;
    };

    return { hiddenCard, ToggleCardBtn };
};

export default function Distributions({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:game:distribution", { game_id: gameId }));
    const [configuration, setConfiguration] = useState(null);
    const [errors, setErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const navigate = useNavigate();
    const { api_post } = useRequest();
    const { distr_user_ip } = parseQS();
    const moderationLocked = gameCommonInfo?.game.locked_by_project_moderation_states;
    const isSuperUser = commonInfo?.account.is_super;

    useEffect(() => {
        if (apiData?.conf) {
            setConfiguration(apiData.conf.configuration);
        }
    }, [apiData?.conf]);

    useEffect(() => {
        if (distr_user_ip && configuration) {
            setShowModal(true);
        }
    }, [configuration]);

    if (!apiData) return;

    const modalClose = () => {
        setShowModal(false);
    };

    const submitConfiguration = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "update_configuration", ...configuration },
            then: (resp) => {
                if (resp.status === "ok") {
                    modalClose();
                    if (distr_user_ip) {
                        navigate(url_reverse("games:distributions", { game_id: gameId }));
                    } else {
                        setApiData(resp);
                        setErrors({});
                    }
                } else {
                    setErrors(resp.errors);
                }
            },
        });
    };

    return (
        <>
            {moderationLocked && (
                <Alert variant="danger">
                    <Trans>
                        Проект находится на модерации, изменение настроек на данном этапе запрещены. Пожалуйста
                        дождитесь завершения процесса модерации.
                    </Trans>
                </Alert>
            )}
            <CommonCard header={header}>
                <DevModal
                    size="xl"
                    show={showModal}
                    onHide={modalClose}
                    header={t("Настройка параметров загрузки дистрибутивов")}
                    body={
                        <>
                            <DistributionConfiguration
                                configuration={configuration}
                                setConfiguration={setConfiguration}
                                errors={errors}
                                disabled={moderationLocked && !isSuperUser}
                            />
                            <Trans>
                                Подробная информация содержится в{" "}
                                <a href="https://documentation.vkplay.ru/f2p_vkp/f2pc_distrib_vkp" target="_blank">
                                    документации
                                </a>{" "}
                                по загрузке дистрибутивов
                            </Trans>
                        </>
                    }
                    footer={
                        <>
                            {Object.keys(errors).length > 0 && (
                                <span className="text-danger">{t("Форма содержит ошибки")}</span>
                            )}
                            <DevForm.Button
                                variant="primary"
                                name="save"
                                onClick={submitConfiguration}
                                disabled={moderationLocked && !isSuperUser}
                            >
                                {t("Сохранить")}
                            </DevForm.Button>
                        </>
                    }
                />

                <Button variant="secondary" onClick={() => setShowModal(true)}>
                    <Awesome type="cog" /> {t("Изменить")}
                </Button>
                <span className="ml-3">
                    {apiData.conf?.date_modified ? (
                        <span className="text-success">
                            {t("Изменение настроек производилось")} {apiData.conf.date_modified}
                        </span>
                    ) : (
                        <span className="text-danger">
                            {t("Для продолжения работы произведите настройку параметров")}{" "}
                            <a href="https://documentation.vkplay.ru/p2p_vkp/p2pc_distrib_vkp">({t("подробнее")})</a>
                        </span>
                    )}
                </span>
            </CommonCard>

            <CardSteamEmu />
            <CardComponents />
            <CardUtilites />
            <CardDistributionList />
        </>
    );
}

function CardSteamEmu() {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const [steamDllUrl, setSteamDllUrl] = useState(null);
    const { hiddenCard, ToggleCardBtn } = useToggleCard("hidden_distr_SteamEmu", gameCommonInfo.game.id);
    const { api_post, api_post_formdata } = useRequest();

    const refreshSteamToken = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "refresh_steam_token" },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData((prev) => ({ ...prev, steam_active_token: resp.steam_active_token }));
                }
            },
        });
    };

    const steamDLLCheck = (ev) => {
        const file = ev.target.files[0];
        if (!file) {
            return;
        }
        const formData = new FormData();
        formData.append("steam_dll", file);
        api_post_formdata(ev, url_reverse("api:gamecenter:steam_dll_check"), formData, {
            then: (resp) => {
                if (resp.status === "ok" && resp.url) {
                    setSteamDllUrl(`<a href="${resp.url}" target="_blank">${resp.url}</a>`);
                }
            },
        });
    };

    const docUrl =
        gameCommonInfo.game.type === GameType.DOWNLOAD
            ? "https://documentation.vkplay.ru/p2p_vkp/p2p_steam_vkp"
            : "https://documentation.vkplay.ru/f2p_vkp/f2pc_steam_vkp";

    return (
        apiData.status === "ok" &&
        (gameCommonInfo.game.is_main_client || gameCommonInfo.game.is_demo) && (
            <>
                <Card className="mt-4">
                    <Card.Header>
                        {t("Эмуляция Steam")}
                        <span className="float-right">{ToggleCardBtn()}</span>
                    </Card.Header>
                    {!hiddenCard && (
                        <Card.Body>
                            <p>
                                <Trans>
                                    Эмуляция Steam позволяет запускать и тестировать игру с интеграцией Steam SDK в
                                    инфраструктуре VK Play. Для этого достаточно заменить steam_api.dll на версию от VK
                                    Play и сгенерировать key (
                                    <a href={docUrl} target="_blank">
                                        подробнее
                                    </a>
                                    )
                                </Trans>
                            </p>
                            <Table bordered>
                                <tbody>
                                    <tr>
                                        <th style={{ width: "40%" }}>{t("ID для эмуляции Steam (Steam «appid»)")}</th>
                                        <td>{gameCommonInfo.game.gc_distrib_id}</td>
                                    </tr>
                                    <tr>
                                        <th>{t("Секрет для эмуляции Steam API (Steam «key»)")}</th>
                                        <td className="dev-inline-control">
                                            <DevForm.TextInput value={apiData.steam_active_token} readOnly={true} />
                                            {apiData.steam_active_token && (
                                                <>
                                                    <a
                                                        className="btn btn-light"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(apiData.steam_active_token);
                                                        }}
                                                    >
                                                        <Awesome type="clipboard" />
                                                    </a>{" "}
                                                </>
                                            )}
                                            <DevForm.Button
                                                variant="light"
                                                name="refresh_steam_token"
                                                onClick={refreshSteamToken}
                                            >
                                                <Awesome type="refresh" />
                                            </DevForm.Button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>{t("Проверка steam.dll")}</th>
                                        <td>
                                            <Form.File onChange={steamDLLCheck} accept=".dll" name="steam_dll_check" />
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>

                            {gameCommonInfo.permissions.can_own && (
                                <div className="mt-4">
                                    <h5>{t("Токены Steam")}</h5>
                                    {apiData.steam_tokens ? (
                                        <Table bordered>
                                            <thead>
                                                <tr>
                                                    <th>{t("Логин")}</th>
                                                    <th>{t("Токен")}</th>
                                                    <th>{t("Активен")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {apiData.steam_tokens?.map((o, i) => {
                                                    return (
                                                        <tr key={`steam_tokens${i}`}>
                                                            <td>{o.login}</td>
                                                            <td>{o.token}</td>
                                                            <td>{o.is_active ? t("Да") : t("Нет")}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <p>{t("Токенов не заведено")}</p>
                                    )}
                                </div>
                            )}
                        </Card.Body>
                    )}
                </Card>

                <DevModal
                    size="lg"
                    show={steamDllUrl}
                    onHide={() => setSteamDllUrl(null)}
                    header={t("Ссылка на steam.dll")}
                    body={parse(steamDllUrl || "")}
                />
            </>
        )
    );
}

function CardComponents() {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { hiddenCard, ToggleCardBtn } = useToggleCard("hidden_distr_Components", gameCommonInfo.game.id);

    return (
        apiData.components &&
        apiData.components.dlc_hid.length + apiData.components.dlc_cont.length > 0 && (
            <Card className="mt-4">
                <Card.Header>
                    {t("Компоненты")}
                    <span className="float-right">
                        <span className="float-right">{ToggleCardBtn()}</span>
                    </span>
                </Card.Header>
                {!hiddenCard && (
                    <Card.Body>
                        <Row>
                            {apiData.components.dlc_hid.length > 0 && (
                                <Col sm={6}>
                                    <strong>{t("Скрытые компоненты")}</strong>
                                    {apiData.components.dlc_hid.map((o) => {
                                        return (
                                            <div key={`hc${o.id}`}>
                                                <Link to={url_reverse("games:page", { game_id: o.id })}>
                                                    {o.__model_locale_object.name}
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </Col>
                            )}
                            {apiData.components.dlc_cont.length > 0 && (
                                <Col sm={6}>
                                    <strong>{t("DLC компоненты")}</strong>
                                    {apiData.components.dlc_cont.map((o) => {
                                        return (
                                            <div key={`dc${o.id}`}>
                                                <Link to={url_reverse("games:page", { game_id: o.id })}>
                                                    {o.__model_locale_object.name}
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </Col>
                            )}
                        </Row>
                    </Card.Body>
                )}
            </Card>
        )
    );
}

function CardUtilites() {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppContext();
    const { api_post } = useRequest();
    const encryptionTokenRef = useRef(null);
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { game } = gameCommonInfo;
    const { hiddenCard, ToggleCardBtn } = useToggleCard("hidden_distr_Utilites", gameCommonInfo.game.id);
    const commonInfo = useRouteLoaderData("commonInfo");
    const drWebRef = useRef(null);
    const gcLoaderUrl = `https://static.gc.vkplay.ru/VKPlayLoader_${game.gc_type_id}_${game.gc_distrib_id}Chnl58a.exe`;
    const moderationLocked = game.locked_by_project_moderation_states;
    const isSuperUser = commonInfo?.account.is_super;

    // Для отслеживания, что логотип DrWeb был показан пользователю
    useEffect(() => {
        const element = drWebRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    tmrPushEvent("dev_dashboard_dr_web_show");
                    observer.disconnect(); // Отключаем после первого срабатывания
                }
            },
            {
                threshold: 0.5, // 50% элемента должно быть видно
            }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, []);

    const refreshAccessToken = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "refresh_access_token" },
            then: (resp) => {
                if (resp.status === "ok") setApiData(resp);
            },
        });
    };

    const enctokenRefresh = (ev) => {
        api_post(ev, apiUrl, {
            params: { action: "refresh_encryption_token" },
            then: (resp) => {
                if (resp.status === "ok") setApiData(resp);
            },
        });
    };

    return (
        apiData.conf?.date_modified && (
            <Card className="mt-4">
                <Card.Header>
                    {t("Утилиты для загрузки дистрибутивов")}
                    <span className="float-right">{ToggleCardBtn()}</span>
                    <a
                        href="https://www.drweb.ru/"
                        target="_blank"
                        rel="noopener"
                        onMouseDown={(e) => {
                            // e.button: 0 - левая, 1 - средняя, 2 - правая
                            if (e.button === 0 || e.button === 1) {
                                tmrPushEvent("dev_dashboard_dr_web_click");
                            }
                        }}
                    >
                        <img
                            ref={drWebRef}
                            className="float-right"
                            src="/showcasestatic/img/logo-dr_web.png"
                            style={{ marginRight: "20px", height: "25px", width: "50px" }}
                            alt="Dr.Web logo"
                        />
                    </a>
                </Card.Header>
                {!hiddenCard && (
                    <Card.Body>
                        <div className="dmg-callout dmg-callout-info">
                            <ul>
                                <li>
                                    <a target="_blank" href="https://static.gc.vkplay.ru/guptools/win64.zip ">
                                        Windows 64 bit
                                    </a>
                                </li>
                                <li>
                                    <a target="_blank" href="https://static.gc.vkplay.ru/guptools/linux.zip ">
                                        Linux 64 bit
                                    </a>
                                </li>
                                <li>
                                    <a target="_blank" href="https://static.gc.vkplay.ru/guptools/darwin.zip ">
                                        MacOS 64 bit
                                    </a>
                                </li>
                            </ul>
                            <p>
                                <Trans>
                                    Распаковать скаченный zip и запускать GameCenterUploader.exe. Подробная информация
                                    содержится в{" "}
                                    <a href="https://documentation.vkplay.ru/f2p_vkp/f2pc_distrib_vkp" target="_blank">
                                        документации
                                    </a>{" "}
                                    по загрузке дистрибутивов.
                                </Trans>
                            </p>
                        </div>
                        <div className="mt-2">
                            <FormFieldGroup label={t("Токен для авторизации в утилитах загрузки дистрибутивов")}>
                                <div className="dev-inline-control">
                                    <DevForm.TextInput value={apiData.distr_token} readOnly={true} />
                                    {apiData.distr_token && (
                                        <>
                                            <a
                                                className="btn btn-light"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(apiData.distr_token);
                                                }}
                                            >
                                                <Awesome type="clipboard" />
                                            </a>{" "}
                                        </>
                                    )}
                                    {(!moderationLocked || isSuperUser) && (
                                        <DevForm.Button
                                            variant="light"
                                            name="refresh_acc_token"
                                            onClick={refreshAccessToken}
                                        >
                                            <Awesome type="refresh" />
                                        </DevForm.Button>
                                    )}
                                </div>
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={t("Ссылка на скачивание загрузчика игры для использования на лендинге")}
                            >
                                <div className="dev-inline-control">
                                    <DevForm.TextInput value={gcLoaderUrl} readOnly={true} />
                                    <a
                                        className="btn btn-light"
                                        onClick={() => {
                                            navigator.clipboard.writeText(gcLoaderUrl);
                                        }}
                                    >
                                        <Awesome type="clipboard" />
                                    </a>
                                </div>
                            </FormFieldGroup>
                            {isSuperUser && (
                                <FormFieldGroup label={t("Ключ шифрования дистрибутива")}>
                                    <div style={{ display: "grid" }}>
                                        <div className="dev-inline-control">
                                            <DevForm.TextArea
                                                innerRef={encryptionTokenRef}
                                                style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                                                name="encryption_secrets"
                                                value={apiData.encryption_secrets}
                                                rows={5}
                                                readOnly={true}
                                                onChange={(ev) =>
                                                    setApiData((prev) => ({
                                                        ...prev,
                                                        [ev.target.name]: ev.target.value,
                                                    }))
                                                }
                                                onBlur={(ev) => {
                                                    if (!encryptionTokenRef.current.readOnly) {
                                                        encryptionTokenRef.current.readOnly = true;
                                                        api_post(ev, apiUrl, {
                                                            params: {
                                                                action: "post_encryption_token",
                                                                secrets: apiData.encryption_secrets,
                                                            },
                                                        });
                                                    }
                                                }}
                                            />
                                            <DevForm.Button
                                                variant="light"
                                                name="edit"
                                                onClick={() => {
                                                    encryptionTokenRef.current.readOnly = false;
                                                    encryptionTokenRef.current.focus();
                                                }}
                                            >
                                                <Awesome type="pencil" />
                                            </DevForm.Button>{" "}
                                            <DevForm.Button
                                                variant="light"
                                                name="refresh_enc_token"
                                                onClick={enctokenRefresh}
                                            >
                                                <Awesome type="refresh" />
                                            </DevForm.Button>
                                        </div>
                                    </div>
                                    <FormFieldHelpText
                                        help_text={t("Каждый токен с новой строки. Первый самый свежий")}
                                    />
                                </FormFieldGroup>
                            )}
                        </div>
                    </Card.Body>
                )}
            </Card>
        )
    );
}

function CardDistributionList() {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppContext();
    const [configuration, setConfiguration] = useState(null);
    const { api_post } = useRequest();
    const toLiveRef = useRef(null);
    const lastHash = useRef("");
    const location = useLocation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const commonInfo = useRouteLoaderData("commonInfo");
    const isSuperUser = commonInfo?.account.is_super;

    const distToLive = (ev) => {
        const ind = ev.target.value;
        const question = t(
            "Обратите внимание, что после публикации билда его нельзя будет перевести обратно в тестовую ветку. Вы уверены, что хотите опубликовать данный билд?"
        );
        if (!confirm(question)) {
            return false;
        }
        api_post(ev, apiUrl, {
            params: { action: "dist_to_live", dist_id: apiData.dists[ind].id },
            then: (resp) => {
                if (resp.status === "ok") setApiData(resp);
            },
        });
    };

    const distWarmingUp = (ind) => {
        api_post(null, apiUrl, {
            params: { action: "dist_warming_up", dist_id: apiData.dists[ind].id },
            then: (resp) => {
                if (resp.status === "ok") setApiData(resp);
            },
        });
    };

    useEffect(() => {
        if (location.hash && toLiveRef.current) {
            if (location.hash.slice(1) === "live") {
                let element = toLiveRef.current;
                while (element.parentElement && element.tagName !== "TR") {
                    element = element.parentElement;
                }
                element.scrollIntoView({ behavior: "smooth", block: "center" });
                element.classList.add("highlight");
                setTimeout(() => {
                    element.classList.remove("highlight");
                }, 2500);
                lastHash.current = "";
            }
        }
    }, []);

    const lastLiveBuild = apiData.dists?.filter((dist) => dist.status === "live")?.slice(-1)[0];
    const build_id = lastLiveBuild?.dst_id;
    const date_on_live = lastLiveBuild?.date_added;
    const tooltipText = gameCommonInfo.game.locked_by_project_moderation_states
        ? t(
              "Перевод в Live недоступен, пока проект находится на модерации.\n" +
                  "Сейчас модератор проверяет билд {{ build_id }} от {{ date_on_live }}.\n" +
                  "Чтобы отправить новый билд, снимите проект с модерации, переведите его в Live и отправьте заново на проверку.",
              { build_id, date_on_live }
          )
        : null;

    return apiData.conf?.date_modified && apiData.dists.length > 0 ? (
        <Card className="mt-4">
            <Card.Header>{t("Загруженные дистрибутивы")}</Card.Header>
            <Card.Body>
                {apiData.quota_size_left > 0 ? (
                    <p className="text-success">
                        <Trans>
                            Квота составляет {{ quota_size: apiData.quota_size }} Гб. Для загрузки доступно{" "}
                            {{ quota_size_left: apiData.quota_size_left }}
                        </Trans>
                    </p>
                ) : (
                    <p className="text-danger">
                        <Trans>
                            Вы достигли лимита по объему дистрибутивов. Удалите ненужные дистрибутивы через утилиту
                            загрузки или обратитесь по почте{" "}
                            <a href="mailto:integration@vk.team">integration@vk.team</a> для увеличения лимита.
                        </Trans>
                    </p>
                )}

                <Table hover responsive className="mt-2 dmg-table">
                    <thead>
                        <tr>
                            <th style={{ width: "7%" }}>{t("ID билда (from)")}</th>
                            <th style={{ width: "7%" }}>{t("ID билда (to)")}</th>
                            <th>{t("Дата создания")}</th>
                            <th>{t("Тип")}</th>
                            <th>{t("Размер")}</th>
                            <th>{t("Автор")}</th>
                            <th>{t("Состояние")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.dists?.map((d, ind) => {
                            return (
                                <tr {...(d.status === "live" ? { name: "live" } : {})} key={`dists${ind}`}>
                                    <td>{d.src_id}</td>
                                    <td>
                                        {d.dst_id} {d.version && <Awesome type="info-circle" tooltip={d.version} />}
                                    </td>
                                    <td>{d.date_added}</td>
                                    <td>{d.src_id === 0 ? t("Дистрибутив") : t("Патч")}</td>
                                    <td>
                                        <Awesome type="info-circle" tooltip={`${d.size} ${t("байт")}`} />{" "}
                                        {d.readable_distr_size}
                                    </td>
                                    <td>{d.author}</td>
                                    <td>
                                        {d.src_id === 0 && d.hotfix && (
                                            <>
                                                <Awesome type="fire" color="red" tooltip="Hotfix" />{" "}
                                            </>
                                        )}
                                        {d.status}{" "}
                                        {d.date_on_live && <Awesome type="info-circle" tooltip={d.date_on_live} />}
                                    </td>
                                    <td>
                                        <Button
                                            size="sm"
                                            variant="light"
                                            onClick={() => setConfiguration(d.configuration)}
                                            disabled={gameCommonInfo.game.locked_by_project_moderation_states}
                                        >
                                            <Awesome type="cogs" />
                                        </Button>
                                        {isSuperUser && (
                                            <>
                                                {" "}
                                                <Awesome
                                                    type="rocket"
                                                    color="orange"
                                                    tooltip="Прогрев дистрибутива"
                                                    onClick={() => distWarmingUp(ind)}
                                                />
                                            </>
                                        )}
                                        {d.status === "test" && ind === apiData.dists.length - 1 && (
                                            <>
                                                {" "}
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        tooltipText ? (
                                                            <Tooltip>
                                                                {tooltipText.split("\n").map((line, idx) => (
                                                                    <div key={idx}>{line}</div>
                                                                ))}
                                                            </Tooltip>
                                                        ) : (
                                                            <></>
                                                        )
                                                    }
                                                    trigger={tooltipText ? ["hover", "focus"] : []}
                                                >
                                                    <DevForm.Button
                                                        size="sm"
                                                        variant="primary"
                                                        name="dist_to_live"
                                                        value={ind}
                                                        disabled={
                                                            gameCommonInfo.game.locked_by_project_moderation_states
                                                        }
                                                        onClick={distToLive}
                                                        innerRef={toLiveRef}
                                                    >
                                                        {t("В Live")}
                                                    </DevForm.Button>
                                                </OverlayTrigger>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                <DevModal
                    size="xl"
                    show={configuration}
                    onHide={() => setConfiguration(null)}
                    header={t("Настройка параметров загрузки дистрибутивов")}
                    body={
                        <DistributionConfiguration
                            configuration={configuration}
                            setConfiguration={setConfiguration}
                            disabled={true}
                        />
                    }
                />
            </Card.Body>
        </Card>
    ) : null;
}

function DistributionConfiguration({ configuration, setConfiguration, errors = {}, disabled = false }) {
    const { t } = useTranslation();
    const [highlights, setHighlights] = useState("");
    const backDropRef = useRef(null);
    const textAreaRef = useRef(null);
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const [currentIpWhiteList, setCurrentIpWhiteList] = useState(null);
    const { distr_user_ip } = parseQS();

    const moment = require("moment");
    const date_launch = gameCommonInfo.game.date_launch ? moment(new Date(gameCommonInfo.game.date_launch)) : null;

    const onSettingsChange = (name, val) => {
        const newConfiguration = { ...configuration };
        newConfiguration[name] = val;
        setConfiguration(newConfiguration);
    };

    const onExtendedParamsChange = (val) => {
        onSettingsChange("ExtendedParams", val);
        const new_val = val.replace(/\n$/g, "\n\n").replace(/ /g, '<a class="highlight_mark">$&</a>');
        setHighlights(new_val);
    };

    const [cloudpathes, setCloudpathes] = useState([]);

    const onCloudChange = (index, name, val) => {
        const newcloudpathes = cloudpathes.slice(0);
        newcloudpathes[index][name] = val;
        setCloudpathes(newcloudpathes);
        onSettingsChange("CloudPathes", JSON.stringify(newcloudpathes));
    };

    const onCloudDelete = (index) => {
        const newcloudpathes = cloudpathes.slice(0);
        newcloudpathes.splice(index, 1);
        setCloudpathes(newcloudpathes);
        onSettingsChange("CloudPathes", JSON.stringify(newcloudpathes));
        return false;
    };

    const onCloudAdd = () => {
        const newcloudpathes = cloudpathes.slice(0);
        newcloudpathes.push({
            path: "SamplePath",
            type: "RoamingAppData",
            mask: "*.save",
            recursive: false,
        });
        setCloudpathes(newcloudpathes);
        onSettingsChange("CloudPathes", JSON.stringify(newcloudpathes));
        return false;
    };

    useEffect(() => {
        if (distr_user_ip) {
            let currentIp = configuration.IPWhitelist || "";
            if (!currentIp.includes(distr_user_ip)) {
                currentIp = currentIp ? `${currentIp},${distr_user_ip}` : distr_user_ip;
            }
            onSettingsChange("IPWhitelist", currentIp);
            setCurrentIpWhiteList(currentIp);
        }
        if (!disabled) {
            onExtendedParamsChange(configuration.ExtendedParams || "");
        }
        setCloudpathes(JSON.parse(configuration.CloudPathes || "[]"));
    }, []);

    const cloudpath_types = [
        { value: "RoamingAppData", label: "RoamingAppData" },
        { value: "LocalAppData", label: "LocalAppData" },
        { value: "LocalAppDataLow", label: "LocalAppDataLow" },
        { value: "SavedGames", label: "SavedGames" },
        { value: "GameRoot", label: "GameRoot" },
        { value: "Documents", label: "Documents" },
    ];

    const cloudpath_examples = {
        RoamingAppData: "C:\\Users\\USERNAME\\AppData\\Roaming",
        LocalAppData: "C:\\Users\\USERNAME\\AppData\\Local",
        LocalAppDataLow: "C:\\Users\\USERNAME\\AppData\\LocalLow",
        SavedGames: "C:\\Users\\USERNAME\\Saved Games",
        Documents: "C:\\Users\\USERNAME\\Documents",
        GameRoot: "D:\\VKPlay\\TheGame",
    };

    const preinstall_choices = [
        { value: "VCREDIST_2015_X86", label: "Redist 2015, 2017, 2019, and 2022 x86" },
        { value: "VCREDIST_2015_X64", label: "Redist 2015, 2017, 2019, and 2022 x64" },
        { value: "VCREDIST_2013_X86", label: "Redist 2013 (VC++ 12.0) x86" },
        { value: "VCREDIST_2013_X64", label: "Redist 2013 (VC++ 12.0) x64" },
        { value: "VCREDIST_2012_X86", label: "Redist 2012 (VC++ 11.0) Update 4 x86" },
        { value: "VCREDIST_2012_X64", label: "Redist 2012 (VC++ 11.0) Update 4 x64" },
        { value: "VCREDIST_2010_X86", label: "Redist 2010 (VC++ 10.0) SP1 (no longer supported) x86" },
        { value: "VCREDIST_2010_X64", label: "Redist 2010 (VC++ 10.0) SP1 (no longer supported) x64" },
        { value: "VCREDIST_2008_X86", label: "Redist 2008 (VC++ 9.0) SP1 (no longer supported) x86" },
        { value: "VCREDIST_2008_X64", label: "Redist 2008 (VC++ 9.0) SP1 (no longer supported) x64" },
        { value: "DXREDIST", label: "DirectX" },
    ];

    const initialAllClientExeFields = configuration.AllClientExe ? configuration.AllClientExe.split(":") : [""];
    const [allClientExeFields, setAllClientFields] = useState(initialAllClientExeFields);

    const onAddAllClientExeField = () => {
        setAllClientFields((prevFields) => [...prevFields, ""]);
    };

    const onRemoveAllClientExeField = (index) => {
        if (allClientExeFields.length > 1) {
            const updatedFields = [...allClientExeFields];
            updatedFields.splice(index, 1);
            setAllClientFields(updatedFields);

            setConfiguration((prev) => ({
                ...prev,
                AllClientExe: updatedFields.join(":"),
            }));
        }
    };

    const onAllClientExeChange = (index, value) => {
        const updatedFields = [...allClientExeFields];
        updatedFields[index] = value;
        setAllClientFields(updatedFields);

        setConfiguration((prev) => ({
            ...prev,
            AllClientExe: updatedFields.join(":"),
        }));
    };

    return (
        <>
            {distr_user_ip && (
                <>
                    <div className="dmg-callout dmg-callout-warning">
                        <b>{t("Проверка IP-адреса для загрузки дистрибутива")}</b>
                        <br />
                        {t(
                            "Ваш текущий IP-адрес ({{distr_user_ip}}) не совпадает с разрешёнными для загрузки дистрибутивов.",
                            {
                                distr_user_ip,
                            }
                        )}
                        <br />
                        <br />
                        {t("Чтобы продолжить процесс загрузки, выполните следующие действия:")}
                        <br />
                        1.{" "}
                        {t("Убедитесь, что текущий IP-адрес корректен и вы хотите его добавить в список разрешённых.")}
                        <br />
                        2. {t("Нажмите кнопку «Сохранить» ниже, чтобы обновить настройки IP-адресов.")}
                        <br />
                        <br />
                        {t("После сохранения попробуйте повторить процесс загрузки.")}
                    </div>
                    <br />
                </>
            )}
            {!gameCommonInfo.game.is_dlc && (
                <>
                    <FormFieldGroup label={t("Имя главного исполняемого файла (32 бита)")}>
                        <DevForm.TextInput
                            value={configuration.ExeFileName}
                            onChange={(ev) => {
                                onSettingsChange("ExeFileName", ev.target.value);
                            }}
                            isInvalid={errors.ExeFileName}
                            disabled={disabled}
                        />
                        <FormFieldErrorMessage error={errors.ExeFileName} custom={true} />
                        <FormFieldHelpText help_text={t('Символы ":", "/", "*" и "?" недопустимы')} />
                    </FormFieldGroup>
                    <FormFieldGroup label={t("Имя главного исполняемого файла (64 бита)")}>
                        <DevForm.TextInput
                            value={configuration.ExeFileName64bit}
                            onChange={(ev) => {
                                onSettingsChange("ExeFileName64bit", ev.target.value);
                            }}
                            isInvalid={errors.ExeFileName64bit}
                            disabled={disabled}
                        />
                        <FormFieldErrorMessage error={errors.ExeFileName64bit} custom={true} />
                        <FormFieldHelpText help_text={t('Символы ":", "/", "*" и "?" недопустимы')} />
                    </FormFieldGroup>
                    {allClientExeFields.map((value, index) => (
                        <FormFieldGroup label={t("Дополнительные исполняемые файлы игры")} key={index}>
                            <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                <DevForm.TextInput
                                    key={index}
                                    value={value}
                                    onChange={(e) => onAllClientExeChange(index, e.target.value)}
                                    style={{ marginRight: "10px", height: "38px" }}
                                    disabled={disabled}
                                />
                                {!disabled && index === 0 && (
                                    <Button
                                        onClick={onAddAllClientExeField}
                                        key={`button-${index}`}
                                        variant="primary"
                                        style={{ height: "38px" }}
                                    >
                                        <Awesome type="plus" />
                                    </Button>
                                )}
                                {!disabled && index > 0 && (
                                    <Button
                                        onClick={() => onRemoveAllClientExeField(index)}
                                        key={`button-${index}`}
                                        variant="primary"
                                        style={{ height: "38px" }}
                                    >
                                        <Awesome type="trash" />
                                    </Button>
                                )}
                            </div>
                            <FormFieldErrorMessage error={errors.AllClientExe} custom={true} />
                            <FormFieldHelpText help_text={t('Символы ":", "/", "*" и "?" недопустимы')} />
                        </FormFieldGroup>
                    ))}
                    <FormFieldGroup label={t("Дополнительные параметры командной строки запуска клиента (32 бита)")}>
                        <DevForm.TextInput
                            value={configuration.ExeParams}
                            onChange={(ev) => {
                                onSettingsChange("ExeParams", ev.target.value);
                            }}
                            isInvalid={errors.ExeParams}
                            disabled={disabled}
                        />
                        <FormFieldErrorMessage error={errors.ExeParams} custom={true} />
                    </FormFieldGroup>
                    <FormFieldGroup label={t("Дополнительные параметры командной строки запуска клиента (64 бита)")}>
                        <DevForm.TextInput
                            value={configuration.ExeParams64bit}
                            onChange={(ev) => {
                                onSettingsChange("ExeParams64bit", ev.target.value);
                            }}
                            isInvalid={errors.ExeParams64bit}
                            disabled={disabled}
                        />
                        <FormFieldErrorMessage error={errors.ExeParams64bit} custom={true} />
                    </FormFieldGroup>
                    <FormFieldGroup label={t("Название меню-группы под кнопкой пуск")}>
                        <DevForm.TextInput value={configuration.StartMenuGroupName} disabled={true} />
                    </FormFieldGroup>
                </>
            )}
            <FormFieldGroup label={t("Расширенные настройки клиента")}>
                <div className="distributions-extendparams-container">
                    {!disabled && (
                        <div ref={backDropRef} className="distributions-extendparams-backdrops">
                            <div className="distributions-extendparams-highlights">{parse(highlights)}</div>
                        </div>
                    )}
                    <DevForm.TextArea
                        innerRef={textAreaRef}
                        className={disabled ? null : "distributions-extendparams-textarea"}
                        name="extendparams"
                        rows={6}
                        value={configuration.ExtendedParams}
                        onChange={(ev) => {
                            onExtendedParamsChange(ev.target.value);
                        }}
                        onScroll={() => {
                            if (disabled) {
                                return;
                            }
                            backDropRef.current.scrollTop = textAreaRef.current.scrollTop;
                            backDropRef.current.scrollLeft = textAreaRef.current.scrollLeft;
                        }}
                        isInvalid={errors.ExtendedParams}
                        disabled={disabled}
                    />
                </div>
                <FormFieldErrorMessage error={errors.ExtendedParams} custom={true} />
                <FormFieldHelpText help_text={t("несколько строк формата: <имя>=<значение>")} />
            </FormFieldGroup>
            <FormFieldGroup label={t("Расширенные настройки Config")}>
                <DevForm.TextArea
                    rows={5}
                    name="extendedconfig"
                    value={configuration.ExtendedConfig}
                    onChange={(ev) => {
                        onSettingsChange("ExtendedConfig", ev.target.value);
                    }}
                    isInvalid={errors.ExtendedConfig}
                    disabled={disabled}
                />
                <FormFieldErrorMessage error={errors.ExtendedConfig} custom={true} />
                <FormFieldHelpText help_text={t("несколько строк формата: <имя>=<значение>")} />
            </FormFieldGroup>
            <FormFieldGroup label={t("IP адреса, с которых будет загружен дистрибутив")}>
                <DevForm.TextArea
                    value={currentIpWhiteList}
                    name="ipwhitelist"
                    onChange={(ev) => {
                        onSettingsChange("IPWhitelist", ev.target.value);
                        setCurrentIpWhiteList(ev.target.value);
                    }}
                    isInvalid={errors.IPWhitelist}
                    disabled={disabled}
                    rows="6"
                />
                <FormFieldErrorMessage error={errors.IPWhitelist} custom={true} />
                <FormFieldHelpText
                    help_text={t(
                        "Введите адреса через запятую. Например, 87.240.159.255/32, 87.240.132.67. Адреса будут автоматически сконвертированы в формат CIDR"
                    )}
                />
                <FormFieldHelpText help_text={t("Чтобы разрешить загрузку с любого IP-адреса, введите 0.0.0.0/0.")} />
            </FormFieldGroup>
            {!gameCommonInfo.game.is_dlc && (
                <FormFieldGroup label={t("Дополнительные установочные компоненты")}>
                    <DevForm.SelectMultiple
                        choices={preinstall_choices}
                        value={configuration.ExtPreinstall?.split(";")}
                        disabled={disabled}
                        onChange={(data) => {
                            onSettingsChange("ExtPreinstall", data.join(";"));
                        }}
                    />
                </FormFieldGroup>
            )}
            <FormFieldGroup label={t("Шифрование")}>
                <DevForm.Checkbox
                    id="encryption"
                    type="switch"
                    checked={configuration.DistEncryption}
                    disabled={gameCommonInfo.game.is_playtest || (date_launch && date_launch < moment())}
                    onChange={(ev) => onSettingsChange("DistEncryption", ev.target.checked)}
                />
                <FormFieldHelpText
                    help_text={t(
                        "Билды с опцией «Шифрование» нельзя скачать и установить без использования Игрового Центра " +
                            'и прав разработчика или бетатестера. Подробнее в <a href="https://documentation.vkplay.ru/f2p_vkp/f2p_encryption" target="_blank">документации</a>.'
                    )}
                />
            </FormFieldGroup>
            {cloudpathes.map((obj, i) => {
                return (
                    <FormFieldGroup label={t("Путь до папки для синхронизации с облаком")} key={i}>
                        <Row className={"mb-1"}>
                            <Col xs={4}>
                                <DevForm.Select
                                    choices={cloudpath_types}
                                    value={obj.type}
                                    onChange={(data) => onCloudChange(i, "type", data.value)}
                                    disabled={disabled}
                                />
                            </Col>
                            <Col xs={5}>
                                <DevForm.TextInput
                                    value={obj.path}
                                    onChange={(ev) => onCloudChange(i, "path", ev.target.value)}
                                    disabled={disabled}
                                />
                            </Col>
                            <Col xs={2}>
                                <DevForm.TextInput
                                    value={obj.mask}
                                    onChange={(ev) => onCloudChange(i, "mask", ev.target.value)}
                                    disabled={disabled}
                                />
                            </Col>
                            <Col>
                                <Button variant="danger" onClick={() => onCloudDelete(i)} disabled={disabled}>
                                    <span className="fa fa-remove"></span>
                                </Button>
                            </Col>
                        </Row>
                        <Row className={"mb-1"}>
                            <Col xs={4}>
                                <DevForm.Checkbox
                                    label={t("Искать в подкаталогах")}
                                    onChange={(ev) => onCloudChange(i, "recursive", ev.target.checked)}
                                    checked={obj.recursive || false}
                                    disabled={disabled}
                                />
                            </Col>
                            <Col xs={8}>
                                <b>Превью:</b>&nbsp;
                                <span
                                    style={{ wordWrap: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}
                                >
                                    {cloudpath_examples[obj.type]}\{obj.path}\{obj.mask}
                                </span>
                            </Col>
                        </Row>
                    </FormFieldGroup>
                );
            })}
            <FormFieldGroup>
                <Button onClick={onCloudAdd} disabled={disabled}>
                    {t("Добавить папку для синхронизации с облаком")}
                </Button>
            </FormFieldGroup>
        </>
    );
}
